import { FC, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import InputBox from 'atoms/components/InputBox';
import SecondaryButton from 'atoms/components/Buttons/Secondary';
import PrimaryButton from 'atoms/components/Buttons/Primary';
import { ReactComponent as PdfIcon } from 'atoms/icons/svg/pdf.svg';
import ImageViewer from './ImageViewer';
import {
  HoleDectionListItem,
  UpdateHoleDetectionType,
} from 'models/holeDetection';
import { MenuItem, Select } from '@mui/material';
import {
  useGetHoleClassificationsQuery,
  useUpdateHoleDetectionMutation,
} from 'store/_Api/HoleDetection';
import useBaseLinks from 'store/Auth/Links/useBaseLinks';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query';
import { ApiErrorShape, FieldErrorShape } from 'models/error';
import toast from 'components/toast';
import { ReactComponent as CopyIcon } from 'atoms/icons/svg/copy.svg';
import { t } from 'i18next';
import ButtonV2 from 'atoms/components/Buttons/V2/ButtonV2';
import './DetectionDetails.scss';

type PropsType = {
  item: HoleDectionListItem;
  isHistory?: boolean;
};

const DetectionDetails: FC<PropsType> = ({ item, isHistory }): JSX.Element => {
  const { getLink } = useBaseLinks();
  const classificationLink = getLink('hole-detection-classifications') ?? '';
  const { data: classificationData } = useGetHoleClassificationsQuery(
    classificationLink ?? skipToken,
  );

  const defaultFieldsValue = {
    risk_classification: item?.ai_risk_classification ?? -1, // -1 is the select classification
    comment: item?.reviewer_comment ?? '',
    vertical_line_breaks: item?.vertical_line_breaks ?? '',
    horizontal_line_breaks: item?.horizontal_line_breaks ?? '',
    escape_risk: item?.escape_risk ?? '',
  };
  const [fields, setFields] =
    useState<UpdateHoleDetectionType>(defaultFieldsValue);

  const [updateHoleDetection, { isLoading }] = useUpdateHoleDetectionMutation();
  const [error, setError] = useState<string | FieldErrorShape[]>('');

  const isActualHole = useMemo(() => {
    return fields.risk_classification === 3;
  }, [fields.risk_classification]);

  const handleChange = (event) => {
    if (isHistory) {
      return false; // on history tab don't update anything
    }
    const { name, value } = event.target;

    if (name === 'escape_risk') {
      setFields((prev) => ({
        ...prev,
        [name]: value === 'true',
      }));

      return;
    }

    setFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const classificationValues = useMemo(() => {
    return classificationData?.collection.map((item) => item.classification);
  }, [classificationData?.collection]);

  const isValid = useMemo(() => {
    if (
      typeof fields?.risk_classification === 'string' ||
      !classificationValues?.includes(fields?.risk_classification)
    ) {
      return false;
    }

    if (isActualHole) {
      if (
        !fields.vertical_line_breaks ||
        !fields.horizontal_line_breaks ||
        typeof fields?.escape_risk !== 'boolean'
      ) {
        return false;
      }
    }

    return true;
  }, [fields, isActualHole]);

  const handleSubmit = async () => {
    if (!item.links?.annotate) {
      setError(t('Access denied, please check your permissions'));
      return;
    }

    if (!isValid) {
      setError(t('Please re-check all fields and try again'));
      return;
    }

    setError('');

    const res = await updateHoleDetection({
      url: item.links.annotate,
      ...fields,
      ...(!isActualHole
        ? {
            vertical_line_breaks: 0,
            horizontal_line_breaks: 0,
            escape_risk: false,
          }
        : {}),
    });

    if ('error' in res) {
      const error = (res.error as FetchBaseQueryError)?.data;
      const errorMessage =
        (error as ApiErrorShape)?.detail ||
        t('Unable to update, Please try again later');
      console.error({ error, errorMessage, res });

      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else {
        setError(errorMessage);
      }

      return;
    }

    toast.success(
      'Hole detection annotation submitted successfully. It has now been moved to history',
    );
  };

  const handleCopyLink = async () => {
    try {
      const currentUrl = window.location.href;

      const urlObj = new URL(currentUrl);
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`;

      const imageId = item?.image_id ?? undefined;
      if (!imageId) {
        toast.error(
          'Could not generate link to hole because of missing image id',
        );
        return;
      }
      const linkToCopy = `${baseUrl}/holes/${imageId}`;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(linkToCopy);
        toast.success('Link copied to clipboard!');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = linkToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link.');
    }
  };

  return (
    <Grid container spacing="30px" className="detect-hole-wrapper">
      <Grid xs={4} item>
        <Typography className="section-heading">{t('Image')}</Typography>
        <ImageViewer image={item?.links?.image} />
      </Grid>

      <Grid xs={4} item>
        <div className="comment">
          <Typography className="section-heading">{t('Comment')}</Typography>
          <InputBox
            multiline
            rows={5}
            fullWidth
            name="comment"
            value={fields.comment}
            onChange={handleChange}
            className="comment-input"
            placeholder={t('A comment about the finding')}
          />
        </div>
      </Grid>

      <Grid item xs={4}>
        <div className="information">
          <div className="information-field">
            <Typography className="section-heading">
              {t('Information')}
            </Typography>

            <TextField
              InputProps={{ readOnly: true }}
              label={t('Position')}
              InputLabelProps={{ shrink: true }}
              value={item?.position}
              variant="standard"
              className="read-only-input"
            />
            <TextField
              InputProps={{ readOnly: true }}
              label={t('Depth')}
              value={item?.depth}
              variant="standard"
              InputLabelProps={{ shrink: true }}
              className="read-only-input"
            />
            <FormControl
              variant="standard"
              className={isHistory ? 'read-only-input' : ''}
              fullWidth
            >
              <InputLabel shrink={true}>{t('Classification')}</InputLabel>
              <Select
                name="risk_classification"
                value={fields.risk_classification}
                onChange={handleChange}
                displayEmpty
                sx={{ height: '40px' }}
                readOnly={isHistory ? true : false}
              >
                <MenuItem value={-1}>{t('Select Classification')}</MenuItem>
                {classificationData?.collection?.map(
                  ({ classification, label }) => (
                    <MenuItem
                      className="classification-options"
                      key={label}
                      value={classification}
                    >
                      {label.replaceAll('_', ' ')}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            {isActualHole && (
              <>
                <div className="hole-demensions">
                  <TextField
                    name="vertical_line_breaks"
                    value={fields.vertical_line_breaks}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    placeholder={t('Enter vertical line breaks')}
                    label={t('Vertical line breaks')}
                    variant="standard"
                    type="number"
                    fullWidth
                    className={isHistory ? 'read-only-input' : ''}
                  />
                  <TextField
                    name="horizontal_line_breaks"
                    value={fields.horizontal_line_breaks}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    placeholder={t('Enter horizontal line breaks')}
                    label={t('Horizontal line breaks')}
                    variant="standard"
                    type="number"
                    fullWidth
                    className={isHistory ? 'read-only-input' : ''}
                  />
                </div>

                <FormControl>
                  <FormLabel className="escape-risk">
                    {t('Escape Risk Status')}
                  </FormLabel>
                  <RadioGroup
                    row
                    name="escape_risk"
                    value={fields.escape_risk}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={t('No escape risk')}
                    />
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t('Escape risk')}
                    />
                  </RadioGroup>
                </FormControl>
              </>
            )}
          </div>

          <Box className="detection-actions">
            {isHistory ? (
              <>
                <ButtonV2
                  type="tertiary"
                  className="copy-btn"
                  onClick={handleCopyLink}
                >
                  {t('Copy link')} <CopyIcon />
                </ButtonV2>
                <SecondaryButton className="export-pdf">
                  <PdfIcon />
                  {t('Export report to pdf')}
                </SecondaryButton>
              </>
            ) : (
              <PrimaryButton
                startIcon={isLoading ? <CircularProgress size="1rem" /> : null}
                onClick={handleSubmit}
                className="action"
                disabled={!isValid}
              >
                {t('Submit')}
              </PrimaryButton>
            )}
          </Box>
        </div>
      </Grid>

      {error && (
        <FormHelperText className="form-error">
          {error as string}
        </FormHelperText>
      )}
    </Grid>
  );
};

export default DetectionDetails;
